import { useState, useEffect, useCallback, useReducer } from 'react';
import {
  ReaderState,
  ReaderAction,
  ReaderSettings,
  Bookmark,
  BacklogEntry,
  DEFAULT_READER_SETTINGS,
} from '../types/reader';
import { TOTAL_PAGES, clampPageNumber, PageData } from '../types/tome';
import { getPage } from '../data/tome';
import {
  initializeStorage,
  loadCurrentPage,
  saveCurrentPage,
  loadSettings,
  saveSettings,
  loadBookmarks,
  saveBookmarks,
  loadBacklog,
  saveBacklog,
  clearAllStorage,
} from '../utils/storage';

function createInitialState(): ReaderState {
  initializeStorage();
  const initialPage = loadCurrentPage();
  const initialSettings = loadSettings();
  const initialBookmarks = loadBookmarks();
  const initialBacklog = loadBacklog();

  return {
    currentPage: initialPage,
    totalPages: TOTAL_PAGES,
    isTocOpen: false,
    isBookmarksOpen: false,
    isBacklogOpen: false,
    isSettingsOpen: false,
    isAudioUnlocked: false,
    isMuted: initialSettings.isMuted ?? false,
    volume: initialSettings.masterVolume ?? 0.8,
    settings: initialSettings,
    bookmarks: initialBookmarks,
    backlog: initialBacklog,
  };
}

function readerReducer(state: ReaderState, action: ReaderAction): ReaderState {
  switch (action.type) {
    case 'GOTO_PAGE': {
      const nextP = clampPageNumber(action.page);
      if (nextP === state.currentPage) return state;

      // Create a backlog entry for the page being departed
      const currData: PageData = getPage(state.currentPage);
      const newEntry: BacklogEntry = {
        id: `bl-${currData.pageNumber}-${Date.now()}`,
        pageNumber: currData.pageNumber,
        title: currData.title,
        chapterTitle: currData.chapterTitle,
        contentArchetype: currData.contentArchetype,
        textSnippet: currData.text[0] ? currData.text[0].slice(0, 160) + '...' : '',
        fullText: currData.text,
        text: currData.text,
        visitedAt: Date.now(),
      };

      // Filter out duplicate consecutive entries and limit to 150
      const filteredBacklog = [
        newEntry,
        ...state.backlog.filter((b) => b.pageNumber !== currData.pageNumber),
      ].slice(0, 150);

      saveCurrentPage(nextP);
      saveBacklog(filteredBacklog);

      return {
        ...state,
        currentPage: nextP,
        backlog: filteredBacklog,
      };
    }
    case 'NEXT_PAGE': {
      if (state.currentPage >= TOTAL_PAGES) return state;
      return readerReducer(state, { type: 'GOTO_PAGE', page: state.currentPage + 1 });
    }
    case 'PREV_PAGE': {
      if (state.currentPage <= 1) return state;
      return readerReducer(state, { type: 'GOTO_PAGE', page: state.currentPage - 1 });
    }
    case 'STEP_PAGES': {
      const target = clampPageNumber(state.currentPage + action.step);
      return readerReducer(state, { type: 'GOTO_PAGE', page: target });
    }
    case 'TOGGLE_TOC': {
      const open = action.open !== undefined ? action.open : !state.isTocOpen;
      return {
        ...state,
        isTocOpen: open,
        isBookmarksOpen: false,
        isBacklogOpen: false,
        isSettingsOpen: false,
      };
    }
    case 'TOGGLE_BOOKMARKS': {
      const open = action.open !== undefined ? action.open : !state.isBookmarksOpen;
      return {
        ...state,
        isBookmarksOpen: open,
        isTocOpen: false,
        isBacklogOpen: false,
        isSettingsOpen: false,
      };
    }
    case 'TOGGLE_BACKLOG': {
      const open = action.open !== undefined ? action.open : !state.isBacklogOpen;
      return {
        ...state,
        isBacklogOpen: open,
        isTocOpen: false,
        isBookmarksOpen: false,
        isSettingsOpen: false,
      };
    }
    case 'TOGGLE_SETTINGS': {
      const open = action.open !== undefined ? action.open : !state.isSettingsOpen;
      return {
        ...state,
        isSettingsOpen: open,
        isTocOpen: false,
        isBookmarksOpen: false,
        isBacklogOpen: false,
      };
    }
    case 'ADD_BOOKMARK': {
      const exists = state.bookmarks.some((b) => b.pageNumber === action.bookmark.pageNumber);
      if (exists) return state;
      const updated = [action.bookmark, ...state.bookmarks];
      saveBookmarks(updated);
      return { ...state, bookmarks: updated };
    }
    case 'REMOVE_BOOKMARK': {
      const updated = state.bookmarks.filter((b) => b.id !== action.id);
      saveBookmarks(updated);
      return { ...state, bookmarks: updated };
    }
    case 'UPDATE_SETTINGS': {
      const updated: ReaderSettings = {
        ...state.settings,
        ...action.settings,
      };
      saveSettings(updated);
      return { ...state, settings: updated };
    }
    case 'SET_AUDIO_UNLOCKED':
      return { ...state, isAudioUnlocked: action.unlocked };
    case 'SET_MUTED': {
      const updatedSettings = { ...state.settings, isMuted: action.muted };
      saveSettings(updatedSettings);
      return { ...state, isMuted: action.muted, settings: updatedSettings };
    }
    case 'SET_VOLUME': {
      const clampedVol = Math.max(0, Math.min(1, action.volume));
      const updatedSettings = { ...state.settings, masterVolume: clampedVol };
      saveSettings(updatedSettings);
      return { ...state, volume: clampedVol, settings: updatedSettings };
    }
    case 'CLEAR_BACKLOG': {
      saveBacklog([]);
      return { ...state, backlog: [] };
    }
    case 'RESET_DATA': {
      clearAllStorage();
      return {
        ...state,
        currentPage: 1,
        settings: { ...DEFAULT_READER_SETTINGS },
        bookmarks: [],
        backlog: [],
        isTocOpen: false,
        isBookmarksOpen: false,
        isBacklogOpen: false,
        isSettingsOpen: false,
      };
    }
    default:
      return state;
  }
}

export function useReaderState() {
  const [state, dispatch] = useReducer(readerReducer, undefined, createInitialState);
  const [transitionDirection, setTransitionDirection] = useState<'next' | 'prev' | 'jump'>('jump');

  const currentPageData = getPage(state.currentPage);

  const gotoPage = useCallback((page: number) => {
    const target = clampPageNumber(page);
    setTransitionDirection(target > state.currentPage ? 'next' : target < state.currentPage ? 'prev' : 'jump');
    dispatch({ type: 'GOTO_PAGE', page: target });
  }, [state.currentPage]);

  const nextPage = useCallback(() => {
    setTransitionDirection('next');
    dispatch({ type: 'NEXT_PAGE' });
  }, []);

  const prevPage = useCallback(() => {
    setTransitionDirection('prev');
    dispatch({ type: 'PREV_PAGE' });
  }, []);

  const stepPages = useCallback((step: number) => {
    setTransitionDirection(step > 0 ? 'next' : 'prev');
    dispatch({ type: 'STEP_PAGES', step });
  }, []);

  const toggleToc = useCallback((open?: boolean) => {
    dispatch({ type: 'TOGGLE_TOC', open });
  }, []);

  const toggleBookmarks = useCallback((open?: boolean) => {
    dispatch({ type: 'TOGGLE_BOOKMARKS', open });
  }, []);

  const toggleBacklog = useCallback((open?: boolean) => {
    dispatch({ type: 'TOGGLE_BACKLOG', open });
  }, []);

  const toggleSettings = useCallback((open?: boolean) => {
    dispatch({ type: 'TOGGLE_SETTINGS', open });
  }, []);

  const closeAllDrawers = useCallback(() => {
    dispatch({ type: 'TOGGLE_TOC', open: false });
    dispatch({ type: 'TOGGLE_BOOKMARKS', open: false });
    dispatch({ type: 'TOGGLE_BACKLOG', open: false });
    dispatch({ type: 'TOGGLE_SETTINGS', open: false });
  }, []);

  const addBookmark = useCallback((note?: string) => {
    const page = currentPageData;
    const newBookmark: Bookmark = {
      id: `bm-${page.pageNumber}-${Date.now()}`,
      pageNumber: page.pageNumber,
      createdAt: Date.now(),
      title: page.title,
      previewSnippet: page.text[0] ? page.text[0].slice(0, 120) + '...' : '',
      textSnippet: page.text[0] ? page.text[0].slice(0, 120) + '...' : '',
      actTitle: page.actTitle,
      note,
    };
    dispatch({ type: 'ADD_BOOKMARK', bookmark: newBookmark });
  }, [currentPageData]);

  const removeBookmark = useCallback((id: string) => {
    dispatch({ type: 'REMOVE_BOOKMARK', id });
  }, []);

  const isCurrentPageBookmarked = state.bookmarks.some(
    (b) => b.pageNumber === state.currentPage
  );

  const updateSettings = useCallback((settings: Partial<ReaderSettings>) => {
    dispatch({ type: 'UPDATE_SETTINGS', settings });
  }, []);

  const setAudioUnlocked = useCallback((unlocked: boolean) => {
    dispatch({ type: 'SET_AUDIO_UNLOCKED', unlocked });
  }, []);

  const toggleMute = useCallback(() => {
    dispatch({ type: 'SET_MUTED', muted: !state.isMuted });
  }, [state.isMuted]);

  const setVolume = useCallback((volume: number) => {
    dispatch({ type: 'SET_VOLUME', volume });
  }, []);

  const clearBacklog = useCallback(() => {
    dispatch({ type: 'CLEAR_BACKLOG' });
  }, []);

  const resetAllData = useCallback(() => {
    dispatch({ type: 'RESET_DATA' });
  }, []);

  // Sync theme to body and root data-theme
  useEffect(() => {
    if (typeof document !== 'undefined') {
      const themeName = state.settings.theme;
      document.body.className = `theme-${themeName.replace(/_/g, '-')}`;
      document.documentElement.setAttribute('data-theme', themeName);
    }
  }, [state.settings.theme]);

  return {
    state,
    currentPageData,
    transitionDirection,
    gotoPage,
    nextPage,
    prevPage,
    stepPages,
    toggleToc,
    toggleBookmarks,
    toggleBacklog,
    toggleSettings,
    closeAllDrawers,
    addBookmark,
    removeBookmark,
    isCurrentPageBookmarked,
    updateSettings,
    setAudioUnlocked,
    toggleMute,
    setVolume,
    clearBacklog,
    resetAllData,
  };
}
